<template>
  <f7-page name="spend-credits">
    <f7-navbar :title="$t('growth.spend.title')" back-link="Back" />
     <AppLayout>
  <template #main>
    <f7-block class="growth-page  no-margin no-padding">
      <f7-block v-if="loading" class="loading-block">
        <f7-preloader color="green" />
      </f7-block>

      <template v-else>
        <f7-card class="growth-card balance-card">
          <f7-card-content>
            <div class="text-align-center">
              <div class="t-h2 t-green t-bold">
                {{ wallet.availableCredit }} <small>{{ wallet.currency || groupStore.activeGroup.currency|| groupStore.activeGroup.defaultCurrency}}</small>
              </div>
              <div class="t-muted t-small">{{ $t('growth.spend.availableBalance') }}</div>
            </div>
          </f7-card-content>
        </f7-card>

        <f7-card class="growth-card">
          <f7-card-header>
            <f7-icon f7="cart" size="22" class="t-blue" />
            <div class="t-bold">{{ $t('growth.spend.spendOptions') }}</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list v-if="spendOptions.length" class="growth-list">
              <f7-list-item
                v-for="opt in spendOptions"
                :key="opt.id"
                :title="opt.name"
                :subtitle="opt.description"
                :footer="$t('growth.spend.cost', { cost: opt.cost })"
                link
                @click="selectOption(opt)"
              >
                <template #after>
                  <f7-button small round raised fill class="btn-green" @click.stop="confirmSpend(opt)">
                    {{ $t('growth.spend.spend') }}
                  </f7-button>
                </template>
              </f7-list-item>
            </f7-list>
            <div v-else class="t-muted text-align-center py-3">{{ $t('growth.spend.noOptions') }}</div>
          </f7-card-content>
        </f7-card>

        <f7-card class="growth-card">
          <f7-card-header>
            <f7-icon f7="list_bullet" size="22" class="t-gold" />
            <div class="t-bold">{{ $t('growth.spend.whatYouCanDo') }}</div>
          </f7-card-header>
          <f7-card-content>
            <f7-list media-list class="growth-list">
              <f7-list-item :title="$t('growth.spend.merchandise')"></f7-list-item>
              <f7-list-item :title="$t('growth.spend.premiumFees')"></f7-list-item>
              <f7-list-item :title="$t('growth.spend.boostListings')"></f7-list-item>
              <f7-list-item :title="$t('growth.spend.subsidiseUsage')"></f7-list-item>
              <f7-list-item :title="$t('growth.spend.contributeOther')"></f7-list-item>
              <f7-list-item :title="$t('growth.spend.donateProjects')"></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>
      </template>
    </f7-block>
       </template>
</AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  f7Page, f7Navbar, f7Block, f7Card, f7CardHeader, f7CardContent,
  f7List, f7ListItem, f7Button, f7Preloader
} from 'framework7-vue'
import { f7 } from 'framework7-vue'
import AppLayout from '../components/common/AppLayout.vue'
import { useGrowthStore } from '../js/pinia-stores/growth.js'
import { useGroupsStore } from '../js/pinia-stores/group.js'

import i18n from '../js/i18n.js'
const { t } = i18n.global

const props = defineProps({ groupId: { type: String, default: '' } })

const growthStore = useGrowthStore()
const groupStore = useGroupsStore()


const groupId = computed(() => props.groupId || '123')
const wallet = computed(() => growthStore.wallet)
const spendOptions = computed(() => growthStore.spendOptions)
const loading = computed(() => growthStore.loading)

onMounted(async () => {
  try {
    await growthStore.fetchSpendOptions(groupId.value)
  } catch (e) {
    console.warn('Growth spend options unavailable:', e)
  }
})

function selectOption(opt) {
  f7.dialog.confirm(
    t('growth.spend.confirmSpendMessage', { cost: opt.cost, name: opt.name }),
    t('growth.spend.confirmSpendTitle'),
    () => confirmSpend(opt)
  )
}

async function confirmSpend(opt) {
  try {
    await growthStore.spendCredits(groupId.value, opt)
    f7.toast.show({
      text: t('growth.spend.purchasedSuccess', { name: opt.name }),
      position: 'bottom',
      closeTimeout: 2000,
    })
  } catch (e) {
    f7.dialog.alert(t('growth.spend.errorSpending', { message: e?.response?.data?.message || e.message }))
  }
}
</script>

<style scoped>
.growth-page {
  --avo-green: var(--f7-theme-color, #1a6b42);
  --avo-green-light: var(--avocash-green-light, #eaf2eb);
  --avo-gold: var(--f7-secondary, #c9a84c);
  --avo-gold-light: var(--avocash-gold-light, #fdf6e3);
  --avo-blue: var(--f7-blue, #3b4fca);
  --avo-blue-light: var(--avocash-blue-light, #ebf0fc);
  --avo-terracotta: var(--f7-error, #ba1a1a);
  --avo-terracotta-light: var(--avocash-terracotta-light, #fdecea);
  --avo-leaf: var(--f7-theme-color, #5f8a4a);
  --avo-purple: var(--f7-purple, #6b46c1);
  --avo-purple-light: var(--avocash-purple-light, #f5f0ff);
  --avo-surface: var(--avocash-surface, #f4f7f2);
  --avo-pit: var(--f7-on-surface, #1c1c1e);
}

.loading-block { text-align: center; padding: 40px 0; }

.t-bold { font-weight: 600; }
.t-h2 { font-size: 30px; line-height: 1.1; }
.t-small { font-size: 12px; }
.t-muted { color: var(--f7-on-surface-variant, #6f7a71); }

.t-green { color: var(--avo-green); }
.t-gold { color: var(--avo-gold); }
.t-blue { color: var(--avo-blue); }

.growth-card { border-radius: 14px; }
.growth-card :deep(.card-header) { font-size: 15px; gap: 8px; }

.balance-card {
  background: linear-gradient(135deg, var(--avo-green-light), transparent);
}

.growth-list :deep(.item-title) { font-size: 14px; }
.growth-list :deep(.item-subtitle) { font-size: 12px; }
.growth-list :deep(.item-footer) { font-size: 11px; }

.btn-green { background: var(--avo-green); color: #fff; }
.btn-green.active-state { opacity: .8; }
</style>
